import React, { useState, useEffect } from 'react';
import { PalletConfig, UnitSystem } from '../types';
import { Plus, Box, Layers, Sparkles, Clipboard, ArrowRight, Loader2 } from 'lucide-react';
import { toStoredLength, toStoredWeight, getLengthLabel, getWeightLabel } from '../utils/units';
import { parseManifest } from '../services/geminiService';
import { translations as t } from '../utils/translations';

interface InputFormProps {
  onAddPallet: (pallet: PalletConfig) => void;
  onImportPallets: (pallets: PalletConfig[]) => void;
  palletCount: number;
  unitSystem: UnitSystem;
}

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6'];

const InputForm: React.FC<InputFormProps> = ({ onAddPallet, onImportPallets, palletCount, unitSystem }) => {
  const [activeTab, setActiveTab] = useState<'manual' | 'ai'>('manual');
  const [isProcessing, setIsProcessing] = useState(false);
  const [importText, setImportText] = useState('');

  // Local state for Manual Input
  const [form, setForm] = useState({
    length: unitSystem === 'metric' ? 120 : 48,
    width: unitSystem === 'metric' ? 80 : 40,
    height: unitSystem === 'metric' ? 150 : 60,
    weight: unitSystem === 'metric' ? 500 : 1100,
    quantity: 1,
    name: unitSystem === 'metric' ? 'Europalet' : 'Standard Pallet',
    stackable: false,
    maxStackWeight: unitSystem === 'metric' ? 1000 : 2200
  });

  // Update defaults when unit system changes
  useEffect(() => {
    if (unitSystem === 'metric') {
      setForm(prev => ({ ...prev, length: 120, width: 80, height: 150, weight: 500, maxStackWeight: 1000 }));
    } else {
      setForm(prev => ({ ...prev, length: 48, width: 40, height: 60, weight: 1100, maxStackWeight: 2200 }));
    }
  }, [unitSystem]);

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAddPallet({
      id: Math.random().toString(36).substr(2, 9),
      name: form.name,
      quantity: Number(form.quantity),
      color: COLORS[palletCount % COLORS.length],
      length: toStoredLength(Number(form.length), unitSystem),
      width: toStoredLength(Number(form.width), unitSystem),
      height: toStoredLength(Number(form.height), unitSystem),
      weight: toStoredWeight(Number(form.weight), unitSystem),
      stackable: form.stackable,
      maxStackWeight: toStoredWeight(Number(form.maxStackWeight), unitSystem)
    });
  };

  const handleAiImport = async () => {
    if (!importText.trim()) return;
    setIsProcessing(true);
    
    try {
      const items = await parseManifest(importText);
      const palletsToAdd: PalletConfig[] = [];
      let addedCount = 0;
      
      items.forEach((item) => {
        if (item.length && item.width && item.height && item.weight) {
             palletsToAdd.push({
                id: Math.random().toString(36).substr(2, 9),
                name: item.name || 'Imported Item',
                quantity: Number(item.quantity) || 1,
                color: COLORS[(palletCount + addedCount) % COLORS.length],
                length: Number(item.length), 
                width: Number(item.width),
                height: Number(item.height),
                weight: Number(item.weight),
                stackable: item.stackable || false,
                maxStackWeight: item.stackable ? (Number(item.weight) * 2) : 0 
             });
             addedCount++;
        }
      });

      if (palletsToAdd.length > 0) {
        onImportPallets(palletsToAdd);
        setImportText('');
        setActiveTab('manual');
      }
      
    } catch (e) {
      console.error("Import failed", e);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : (name === 'name' ? value : value)
    }));
  };

  const renderUnitInput = (id: string, name: keyof typeof form, label: string, unit: string) => (
    <div>
        <label htmlFor={id} className="block text-slate-400 text-[10px] mb-1 uppercase font-bold tracking-wider">{label}</label>
        <div className="relative">
            <input 
                id={id}
                type="number" 
                name={name} 
                value={form[name] as number} 
                step="0.1" 
                onChange={handleChange}
                className="w-full bg-slate-900 border border-slate-600 rounded p-2.5 pr-10 text-white focus:ring-2 focus:ring-blue-500 outline-none font-mono"
            />
            <span className="absolute right-3 top-2.5 text-slate-500 text-xs font-bold pointer-events-none">{unit}</span>
        </div>
    </div>
  );

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-lg overflow-hidden flex flex-col h-full">
      <div className="flex border-b border-slate-700" role="tablist">
        <button 
          role="tab"
          aria-selected={activeTab === 'manual'}
          onClick={() => setActiveTab('manual')}
          className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${activeTab === 'manual' ? 'bg-slate-800 text-blue-400 border-b-2 border-blue-400' : 'bg-slate-900/50 text-slate-400 hover:text-slate-200'}`}
        >
          <Box className="w-4 h-4" aria-hidden="true" /> Manual Input
        </button>
        <button 
          role="tab"
          aria-selected={activeTab === 'ai'}
          onClick={() => setActiveTab('ai')}
          className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500 ${activeTab === 'ai' ? 'bg-slate-800 text-purple-400 border-b-2 border-purple-400' : 'bg-slate-900/50 text-slate-400 hover:text-slate-200'}`}
        >
          <Sparkles className="w-4 h-4" aria-hidden="true" /> AI Import
        </button>
      </div>

      <div className="p-5 flex-1 overflow-y-auto">
        {activeTab === 'manual' ? (
          <form onSubmit={handleManualSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label htmlFor="pallet-name" className="block text-slate-400 text-[10px] mb-1 uppercase font-bold tracking-wider">{t.name}</label>
                <input 
                  id="pallet-name"
                  type="text" name="name" value={form.name} onChange={handleChange}
                  className="w-full bg-slate-900 border border-slate-600 rounded p-2.5 text-white focus:ring-2 focus:ring-blue-500 outline-none transition"
                  placeholder="e.g. Euro Pallet"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
               <div>
                 <label htmlFor="pallet-qty" className="block text-slate-400 text-[10px] mb-1 uppercase font-bold tracking-wider">{t.quantity}</label>
                 <div className="relative">
                    <input 
                        id="pallet-qty"
                        type="number" name="quantity" min="1" max="60" value={form.quantity} onChange={handleChange}
                        className="w-full bg-slate-900 border border-slate-600 rounded p-2.5 text-white focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                    />
                 </div>
              </div>
              {renderUnitInput("pallet-weight", "weight", t.weight, getWeightLabel(unitSystem))}
            </div>

            <div className="grid grid-cols-3 gap-3">
              {renderUnitInput("pallet-len", "length", t.length, getLengthLabel(unitSystem))}
              {renderUnitInput("pallet-wid", "width", t.width, getLengthLabel(unitSystem))}
              {renderUnitInput("pallet-hgt", "height", t.height, getLengthLabel(unitSystem))}
            </div>

            {/* Stacking Options */}
            <div className="bg-slate-900/50 p-3 rounded border border-slate-700">
              <div className="flex items-center gap-2 mb-3">
                <input 
                  type="checkbox" 
                  id="stackable"
                  name="stackable" 
                  checked={form.stackable}
                  onChange={handleChange}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <label htmlFor="stackable" className="text-sm text-white font-medium select-none cursor-pointer flex items-center gap-2">
                  <Layers size={14} aria-hidden="true" /> {t.stackable}
                </label>
              </div>
              
              {form.stackable && (
                <div>
                   {renderUnitInput("max-stack", "maxStackWeight", t.maxStackWeight, getWeightLabel(unitSystem))}
                </div>
              )}
            </div>

            <button 
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-4 rounded-lg transition shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
            >
              <Plus size={18} aria-hidden="true" />
              {t.addPallets}
            </button>
          </form>
        ) : (
          <div className="flex flex-col h-full space-y-4">
             <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
                <h4 className="text-purple-300 text-sm font-bold flex items-center gap-2 mb-1">
                   <Sparkles size={14} aria-hidden="true" /> AI Smart Import
                </h4>
                <p className="text-xs text-slate-300">
                   Paste your cargo list. I'll figure out dimensions ({getLengthLabel(unitSystem)}) and weights ({getWeightLabel(unitSystem)}).
                </p>
             </div>
             
             <div className="flex-1 relative">
                <label htmlFor="ai-import-area" className="sr-only">Paste cargo list here</label>
                <textarea 
                   id="ai-import-area"
                   className="w-full h-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-sm text-white resize-none focus:ring-2 focus:ring-purple-500 outline-none"
                   placeholder={`Example:\n5 Pallets 120x80x150 ${getWeightLabel(unitSystem) === 'kg' ? '500kg' : '1100lb'}`}
                   value={importText}
                   onChange={(e) => setImportText(e.target.value)}
                />
             </div>

             <button 
               onClick={handleAiImport}
               disabled={isProcessing || !importText}
               className={`w-full font-bold py-3 px-4 rounded-lg transition shadow-lg flex items-center justify-center gap-2 focus:ring-2 focus:ring-purple-400 focus:outline-none
                 ${isProcessing || !importText 
                   ? 'bg-slate-700 text-slate-500 cursor-not-allowed' 
                   : 'bg-purple-600 hover:bg-purple-500 text-white shadow-purple-600/20'}`}
             >
               {isProcessing ? (
                 <>
                   <Loader2 size={18} className="animate-spin" aria-hidden="true" /> Processing...
                 </>
               ) : (
                 <>
                   Process Cargo <ArrowRight size={18} aria-hidden="true" />
                 </>
               )}
             </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InputForm;